/**
 * Utility functions for task management and calculations
 */

import { formatDateForDB } from './dateUtils';
import type { Task } from '../types';

interface FlatTask extends Task {
  level: number;
  parentId: string | null;
  hasChildren: boolean;
}

export const generateId = (): string => {
  return Date.now() + Math.random().toString(36).substr(2, 9);
};

export const flattenTasks = (tasks: Task[], level: number = 0, parentId: string | null = null): FlatTask[] => {
  let result: FlatTask[] = [];

  tasks.forEach(task => {
    const taskWithMeta: FlatTask = {
      ...task,
      level,
      parentId,
      hasChildren: !!(task.children && task.children.length > 0)
    };

    result.push(taskWithMeta);

    if (task.children && task.expanded !== false) {
      result = result.concat(flattenTasks(task.children, level + 1, task.id));
    }
  });

  return result;
};

/**
 * Como `flattenTasks`, mas ignora `expanded`: desce a árvore INTEIRA.
 *
 * `flattenTasks` existe para desenhar a tela, e por isso respeita o que o usuário colapsou.
 * Arquivo de DADOS (XLSX, CSV) não pode herdar isso: um PMO que fechou "Fase 2" antes de
 * exportar levava a planilha com 260 de 300 tarefas, e as somas de horas por pessoa saíam
 * menores, sem nenhum aviso. Foto da tela (PDF, PNG) continua usando `flattenTasks`: ali o
 * combinado é justamente retratar o que está na tela.
 */
export const flattenAllTasks = (tasks: Task[], level: number = 0, parentId: string | null = null): FlatTask[] => {
  let result: FlatTask[] = [];

  tasks.forEach(task => {
    result.push({
      ...task,
      level,
      parentId,
      hasChildren: !!(task.children && task.children.length > 0),
    });

    if (task.children && task.children.length > 0) {
      result = result.concat(flattenAllTasks(task.children, level + 1, task.id));
    }
  });

  return result;
};

export const findTaskById = (tasks: Task[], id: string): Task | null => {
  for (const task of tasks) {
    if (task.id === id) return task;
    if (task.children) {
      const found = findTaskById(task.children, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Espelho camelCase -> snake_case dos campos que existem nas DUAS grafias.
 *
 * `Task` estende `TaskRow`, então cada tarefa carrega o mesmo dado duas vezes: a
 * string crua do banco (`end_date`) e a versão já parseada (`endDate`). Quem
 * edita escreve só a camelCase. Sem este espelho, a grafia do banco congela no
 * valor de quando o projeto foi carregado e só volta ao normal num F5.
 *
 * Foi exatamente esse o bug em que o Gantt mostrava fim 23/09 e o card do Kanban
 * mostrava 22/09 para a mesma tarefa: o Gantt lê `endDate` e o Kanban lê
 * `end_date`. Só `custom_fields` e `dependency_lags` estavam cobertos aqui.
 *
 * Mapa explícito de propósito. Incluir um campo tem que ser decisão visível e
 * greppável, não efeito colateral de spread. `duration` não entra porque a chave
 * é a mesma nas duas grafias e o spread já resolve.
 */
const ESPELHO_SNAKE_CASE: ReadonlyArray<{
  camel: keyof Task;
  snake: string;
  converte?: (_valor: unknown) => unknown;
}> = [
  { camel: 'startDate', snake: 'start_date', converte: v => formatDateForDB(v as Date | string | null) },
  { camel: 'endDate', snake: 'end_date', converte: v => formatDateForDB(v as Date | string | null) },
  { camel: 'baselineStartDate', snake: 'baseline_start_date', converte: v => formatDateForDB(v as Date | string | null) },
  { camel: 'baselineEndDate', snake: 'baseline_end_date', converte: v => formatDateForDB(v as Date | string | null) },
  { camel: 'customFields', snake: 'custom_fields' },
  { camel: 'dependencyLags', snake: 'dependency_lags' },
];

export const updateTaskInTree = (tasks: Task[], taskId: string, updates: Partial<Task>): Task[] => {
  return tasks.map(task => {
    if (task.id === taskId) {
      const merged = { ...task, ...updates } as Task;
      for (const { camel, snake, converte } of ESPELHO_SNAKE_CASE) {
        if (camel in updates) {
          const valor = updates[camel];
          (merged as unknown as Record<string, unknown>)[snake] = converte ? converte(valor) : valor;
        }
      }
      return merged;
    }
    if (task.children) {
      return {
        ...task,
        children: updateTaskInTree(task.children, taskId, updates)
      };
    }
    return task;
  });
};

export const deleteTaskFromTree = (tasks: Task[], taskId: string): Task[] => {
  return tasks.filter(task => {
    if (task.id === taskId) return false;
    if (task.children) {
      task.children = deleteTaskFromTree(task.children, taskId);
    }
    return true;
  });
};

export const addTaskToTree = (tasks: Task[], parentId: string | null, newTask: Task): Task[] => {
  if (!parentId) {
    return [...tasks, newTask];
  }

  return tasks.map(task => {
    if (task.id === parentId) {
      const children = task.children || [];
      return {
        ...task,
        children: [...children, newTask],
        expanded: true
      };
    }
    if (task.children) {
      return {
        ...task,
        children: addTaskToTree(task.children, parentId, newTask)
      };
    }
    return task;
  });
};

export const toggleTaskExpansion = (tasks: Task[], taskId: string): Task[] => {
  return tasks.map(task => {
    if (task.id === taskId) {
      return { ...task, expanded: !task.expanded };
    }
    if (task.children) {
      return {
        ...task,
        children: toggleTaskExpansion(task.children, taskId)
      };
    }
    return task;
  });
};

export const calculateProgress = (task: Task): number => {
  if (!task.children || task.children.length === 0) {
    return task.progress || 0;
  }

  const childProgress = task.children.reduce((sum: number, child: Task) => {
    return sum + calculateProgress(child);
  }, 0);

  return childProgress / task.children.length;
};

export const getTaskDuration = (startDate: Date | string | null, endDate: Date | string | null, format: 'days' | 'hours' = 'days'): string | number => {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diff = Math.abs(end.getTime() - start.getTime());
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;

  if (format === 'hours') {
    return (days * 8).toFixed(1) + 'h';
  }

  return days + 'd';
};

/**
 * Move uma tarefa para cima dentro do mesmo nível/pai
 * @param {Array} tasks - Árvore de tarefas
 * @param {string} taskId - ID da tarefa a mover
 * @returns {Array} Nova árvore de tarefas
 */
export const moveTaskUp = (tasks: Task[], taskId: string): Task[] => {
  const flatList = flattenTasks(tasks);
  const taskIndex = flatList.findIndex(t => t.id === taskId);

  if (taskIndex <= 0) return tasks; // Já é o primeiro

  const task = flatList[taskIndex];

  // Encontrar a tarefa irmã acima (mesmo pai e mesmo nível)
  let siblingAboveIndex = -1;
  for (let i = taskIndex - 1; i >= 0; i--) {
    const candidate = flatList[i];
    if (candidate.parentId === task.parentId && candidate.level === task.level) {
      siblingAboveIndex = i;
      break;
    }
    // Se encontrou uma tarefa de nível menor, não há irmão acima
    if (candidate.level < task.level) break;
  }

  if (siblingAboveIndex === -1) return tasks; // Não há irmão acima

  // Trocar posições no array do pai
  return swapSiblingTasks(tasks, taskId, flatList[siblingAboveIndex].id);
};

/**
 * Move uma tarefa para baixo dentro do mesmo nível/pai
 * @param {Array} tasks - Árvore de tarefas
 * @param {string} taskId - ID da tarefa a mover
 * @returns {Array} Nova árvore de tarefas
 */
export const moveTaskDown = (tasks: Task[], taskId: string): Task[] => {
  const flatList = flattenTasks(tasks);
  const taskIndex = flatList.findIndex(t => t.id === taskId);

  if (taskIndex === -1 || taskIndex >= flatList.length - 1) return tasks;

  const task = flatList[taskIndex];

  // Encontrar a tarefa irmã abaixo (mesmo pai e mesmo nível)
  // Precisa pular os filhos da tarefa atual se houver
  let siblingBelowIndex = -1;
  let skipChildren = true;

  for (let i = taskIndex + 1; i < flatList.length; i++) {
    const candidate = flatList[i];

    // Se ainda estamos nos filhos da tarefa atual, pular
    if (skipChildren && candidate.level > task.level) continue;
    skipChildren = false;

    // Se encontrou uma tarefa de nível menor, não há irmão abaixo
    if (candidate.level < task.level) break;

    if (candidate.parentId === task.parentId && candidate.level === task.level) {
      siblingBelowIndex = i;
      break;
    }
  }

  if (siblingBelowIndex === -1) return tasks; // Não há irmão abaixo

  // Trocar posições no array do pai
  return swapSiblingTasks(tasks, taskId, flatList[siblingBelowIndex].id);
};

/**
 * Troca duas tarefas irmãs de posição
 * @param {Array} tasks - Árvore de tarefas
 * @param {string} taskId1 - ID da primeira tarefa
 * @param {string} taskId2 - ID da segunda tarefa
 * @returns {Array} Nova árvore de tarefas
 */
const swapSiblingTasks = (tasks: Task[], taskId1: string, taskId2: string): Task[] => {
  // Função auxiliar para trocar no mesmo array
  const swapInArray = (arr: Task[]): Task[] => {
    const idx1 = arr.findIndex(t => t.id === taskId1);
    const idx2 = arr.findIndex(t => t.id === taskId2);

    if (idx1 !== -1 && idx2 !== -1) {
      const newArr = [...arr];
      [newArr[idx1], newArr[idx2]] = [newArr[idx2], newArr[idx1]];
      // Atualizar positions
      return newArr.map((t, i) => ({ ...t, position: i }));
    }

    // Procurar nos filhos
    return arr.map(task => {
      if (task.children && task.children.length > 0) {
        return { ...task, children: swapInArray(task.children) };
      }
      return task;
    });
  };

  return swapInArray(tasks);
};

/**
 * Aumenta o recuo (indent) - transforma tarefa em filha da tarefa acima
 * @param {Array} tasks - Árvore de tarefas
 * @param {string} taskId - ID da tarefa a indentar
 * @returns {Array} Nova árvore de tarefas ou null se não for possível
 */
export const indentTask = (tasks: Task[], taskId: string): Task[] | null => {
  const flatList = flattenTasks(tasks);
  const taskIndex = flatList.findIndex(t => t.id === taskId);

  if (taskIndex <= 0) return null; // Primeira tarefa não pode indentar

  const task = flatList[taskIndex];

  // Encontrar potencial novo pai (tarefa imediatamente acima no mesmo nível ou nível acima)
  let potentialParent: FlatTask | null = null;
  for (let i = taskIndex - 1; i >= 0; i--) {
    const candidate = flatList[i];
    // O novo pai deve estar no mesmo nível ou acima
    if (candidate.level <= task.level) {
      potentialParent = candidate;
      break;
    }
  }

  if (!potentialParent) return null;

  // Remover tarefa da posição atual e adicionar como filha do novo pai
  const taskToMove = findTaskById(tasks, taskId);
  if (!taskToMove) return null;

  // Remover da posição atual
  let newTasks = removeTaskFromParent(tasks, taskId);

  // Adicionar como filha do novo pai
  newTasks = addTaskAsChild(newTasks, potentialParent.id, { ...taskToMove, children: taskToMove.children || [] });

  return newTasks;
};

/**
 * Reduz o recuo (outdent) - move tarefa para o nível do pai
 * @param {Array} tasks - Árvore de tarefas
 * @param {string} taskId - ID da tarefa
 * @returns {Array} Nova árvore de tarefas ou null se não for possível
 */
export const outdentTask = (tasks: Task[], taskId: string): Task[] | null => {
  const flatList = flattenTasks(tasks);
  const task = flatList.find(t => t.id === taskId);

  if (!task || !task.parentId) return null; // Raiz não pode fazer outdent

  // Encontrar o pai e o avô
  const parent = flatList.find(t => t.id === task.parentId);
  if (!parent) return null;

  const taskToMove = findTaskById(tasks, taskId);
  if (!taskToMove) return null;

  // Remover da posição atual
  let newTasks = removeTaskFromParent(tasks, taskId);

  // Adicionar como irmã do pai (no nível do avô)
  if (parent.parentId) {
    // Tem avô - adicionar como filha do avô, após o pai
    newTasks = addTaskAfterSibling(newTasks, parent.parentId, parent.id, { ...taskToMove, children: taskToMove.children || [] });
  } else {
    // Pai é raiz - adicionar na raiz, após o pai
    newTasks = addTaskAfterInRoot(newTasks, parent.id, { ...taskToMove, children: taskToMove.children || [] });
  }

  return newTasks;
};

/**
 * Remove uma tarefa do seu pai atual (não deleta, só remove da hierarquia)
 */
const removeTaskFromParent = (tasks: Task[], taskId: string): Task[] => {
  return tasks.map(task => {
    if (task.children) {
      const childIndex = task.children.findIndex(c => c.id === taskId);
      if (childIndex !== -1) {
        const newChildren = [...task.children];
        newChildren.splice(childIndex, 1);
        return { ...task, children: newChildren };
      }
      return { ...task, children: removeTaskFromParent(task.children, taskId) };
    }
    return task;
  }).filter(task => task.id !== taskId); // Remove da raiz se estiver lá
};

/**
 * Adiciona uma tarefa como filha de outra
 */
const addTaskAsChild = (tasks: Task[], parentId: string, taskToAdd: Task): Task[] => {
  return tasks.map(task => {
    if (task.id === parentId) {
      const children = task.children || [];
      return {
        ...task,
        children: [...children, taskToAdd],
        expanded: true
      };
    }
    if (task.children) {
      return { ...task, children: addTaskAsChild(task.children, parentId, taskToAdd) };
    }
    return task;
  });
};

/**
 * Adiciona uma tarefa após um irmão específico dentro de um pai
 */
const addTaskAfterSibling = (tasks: Task[], parentId: string, siblingId: string, taskToAdd: Task): Task[] => {
  return tasks.map(task => {
    if (task.id === parentId) {
      const children = task.children || [];
      const siblingIndex = children.findIndex(c => c.id === siblingId);
      const newChildren = [...children];
      newChildren.splice(siblingIndex + 1, 0, taskToAdd);
      return { ...task, children: newChildren };
    }
    if (task.children) {
      return { ...task, children: addTaskAfterSibling(task.children, parentId, siblingId, taskToAdd) };
    }
    return task;
  });
};

/**
 * Adiciona uma tarefa após outra na raiz
 */
const addTaskAfterInRoot = (tasks: Task[], afterId: string, taskToAdd: Task): Task[] => {
  const afterIndex = tasks.findIndex(t => t.id === afterId);
  if (afterIndex === -1) return [...tasks, taskToAdd];

  const newTasks = [...tasks];
  newTasks.splice(afterIndex + 1, 0, taskToAdd);
  return newTasks;
};

/**
 * Insere um nó (com sua subárvore) imediatamente antes/depois de targetId,
 * em qualquer nível da árvore (raiz ou dentro de um pai).
 */
const insertRelativeToTarget = (
  tasks: Task[],
  targetId: string,
  node: Task,
  position: 'before' | 'after'
): Task[] => {
  // Target está na raiz?
  const rootIndex = tasks.findIndex(t => t.id === targetId);
  if (rootIndex !== -1) {
    const newTasks = [...tasks];
    newTasks.splice(position === 'after' ? rootIndex + 1 : rootIndex, 0, node);
    return newTasks;
  }

  return tasks.map(task => {
    if (task.children && task.children.length > 0) {
      const childIndex = task.children.findIndex(c => c.id === targetId);
      if (childIndex !== -1) {
        const newChildren = [...task.children];
        newChildren.splice(position === 'after' ? childIndex + 1 : childIndex, 0, node);
        return { ...task, children: newChildren, expanded: true };
      }
      return { ...task, children: insertRelativeToTarget(task.children, targetId, node, position) };
    }
    return task;
  });
};

/**
 * Move uma tarefa (com sua subárvore) para ficar antes/depois da tarefa de
 * destino, ADOTANDO o mesmo pai do destino. Funciona entre níveis diferentes
 * (tirar de um épico, mover para outro épico, ou para a raiz).
 *
 * Padrão MS Project / Jira: a posição de soltura define a participação na
 * hierarquia. Retorna null se o movimento for inválido (soltar dentro da
 * própria subárvore — geraria ciclo).
 */
export const moveTaskToTarget = (
  tasks: Task[],
  movedId: string,
  targetId: string,
  position: 'before' | 'after' = 'before'
): Task[] | null => {
  if (movedId === targetId) return null;

  const moved = findTaskById(tasks, movedId);
  if (!moved) return null;

  // Não permitir soltar dentro da própria subárvore (evita ciclo)
  if (moved.children && findTaskById(moved.children, targetId)) return null;

  const nodeToMove: Task = { ...moved, children: moved.children || [] };

  const without = removeTaskFromParent(tasks, movedId);
  return insertRelativeToTarget(without, targetId, nodeToMove, position);
};

// Mantém a função antiga para compatibilidade (deprecated)
export const moveTask = (tasks: Task[], taskId: string, direction: 'up' | 'down'): Task[] => {
  if (direction === 'up') return moveTaskUp(tasks, taskId);
  if (direction === 'down') return moveTaskDown(tasks, taskId);
  return tasks;
};

/**
 * Adiciona uma tarefa logo após outra (como irmã)
 * Funciona tanto para tarefas na raiz quanto para tarefas dentro de um pai
 */
export const addTaskAfterTask = (tasks: Task[], afterTaskId: string, parentId: string | null, newTask: Task): Task[] => {
  if (!parentId) {
    // Tarefa está na raiz
    return addTaskAfterInRoot(tasks, afterTaskId, newTask);
  } else {
    // Tarefa está dentro de um pai
    return addTaskAfterSibling(tasks, parentId, afterTaskId, newTask);
  }
};

export const duplicateTask = (task: Task): Task => {
  const newTask: Task = {
    ...task,
    id: generateId(),
    name: task.name + ' (Cópia)',
    // Copiar array de dependencies (não apenas a referência)
    dependencies: task.dependencies ? [...task.dependencies] : []
  };

  if (task.children) {
    newTask.children = task.children.map(duplicateTask);
  }

  return newTask;
};

export const getTaskPath = (tasks: Task[], taskId: string, path: string[] = []): string[] | null => {
  for (const task of tasks) {
    if (task.id === taskId) {
      return [...path, task.name];
    }
    if (task.children) {
      const found = getTaskPath(task.children, taskId, [...path, task.name]);
      if (found) return found;
    }
  }
  return null;
};

export const calculateCriticalPath = (tasks: Task[]): FlatTask[] => {
  // Simplified critical path calculation
  // In a real app, this would analyze dependencies and durations
  const allTasks = flattenTasks(tasks);
  return allTasks.filter(task => !task.children || task.children.length === 0);
};

export const validateTaskDates = (task: Task): string[] => {
  const errors: string[] = [];

  if (!task.startDate) {
    errors.push('Data de início é obrigatória');
  }

  if (!task.endDate) {
    errors.push('Data de término é obrigatória');
  }

  if (task.startDate && task.endDate && new Date(task.endDate) < new Date(task.startDate)) {
    errors.push('Data de término deve ser posterior à data de início');
  }

  return errors;
};
